import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Combobox } from "@/components/flexkit/combobox/Combobox";
import { useListMembersInfinite, useMembersByIds, type PlainOrganizationMember } from "@/queries/organization-queries";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

export const ALL_CREATORS_VALUE = "all";
export type CreatorFilterValue = string;

type AutomationCreatorFilterProps = {
    value: CreatorFilterValue;
    onChange: (value: CreatorFilterValue) => void;
};

type CreatorOption = {
    id: string;
    name: string;
    avatarUrl?: string;
};

const ALL_CREATORS_OPTION: CreatorOption = {
    id: ALL_CREATORS_VALUE,
    name: "Everyone",
};

const CreatorAvatar: FC<{ option: CreatorOption }> = ({ option }) => {
    return (
        <Avatar size={16}>
            {option.avatarUrl && <AvatarImage src={option.avatarUrl} alt={`${option.name}'s avatar`} />}
            <AvatarFallback>
                <AvatarInitials name={option.name} size={16} />
            </AvatarFallback>
        </Avatar>
    );
};

export const AutomationCreatorFilter: FC<AutomationCreatorFilterProps> = ({ value, onChange }) => {
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 250, { trailing: true });

    const {
        data: membersData,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
    } = useListMembersInfinite({
        search: debouncedSearch.trim(),
    });

    // Fetch the selected member for display when it's not in the current search results
    const selectedUserId = value !== ALL_CREATORS_VALUE ? value : undefined;
    const { members: selectedMemberMap } = useMembersByIds(selectedUserId ? [selectedUserId] : []);

    const members = useMemo((): PlainOrganizationMember[] => {
        if (!membersData) return [];
        return membersData.pages.flatMap((page) => page.members);
    }, [membersData]);

    const handleScrollEnd = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const creatorOptions = useMemo(() => {
        const options: CreatorOption[] = [ALL_CREATORS_OPTION];

        for (const member of members) {
            options.push({
                id: member.userId,
                name: member.fullName,
                avatarUrl: member.avatarUrl,
            });
        }

        return options;
    }, [members]);

    const selectedOption = useMemo((): CreatorOption => {
        const fromOptions = creatorOptions.find((opt) => opt.id === value);
        if (fromOptions) {
            return fromOptions;
        }

        // Resolve from targeted fetch when the selected user isn't in the current search results
        if (selectedUserId) {
            const member = selectedMemberMap.get(selectedUserId);
            if (member) {
                return {
                    id: member.userId,
                    name: member.fullName,
                    avatarUrl: member.avatarUrl,
                };
            }
        }

        return ALL_CREATORS_OPTION;
    }, [creatorOptions, value, selectedUserId, selectedMemberMap]);

    return (
        <Combobox value={selectedOption.id} onValueChange={onChange} filterPlaceholder="Search people">
            <Combobox.Value>
                {() => (
                    <div className="flex min-w-0 items-center gap-1.5">
                        {selectedOption.id !== ALL_CREATORS_VALUE && <CreatorAvatar option={selectedOption} />}
                        <Combobox.ValueLabel className="min-w-0">{selectedOption.name}</Combobox.ValueLabel>
                    </div>
                )}
            </Combobox.Value>
            <Combobox.Popover sameWidth={false} className="min-w-[var(--popover-anchor-width)] max-w-xs">
                <Combobox.SearchBox onValueChanged={setSearch} loading={isFetching} />
                <Combobox.List
                    items={creatorOptions}
                    searchKeys={["name"]}
                    disableFiltering
                    onScrollEnd={handleScrollEnd}
                    noMatchesComponent={<Combobox.Empty>No creators found</Combobox.Empty>}
                >
                    {(option) => (
                        <Combobox.ListItem key={option.id} value={option.id}>
                            {option.id !== ALL_CREATORS_VALUE ? (
                                <Combobox.ListItemLeadingIcon>
                                    <CreatorAvatar option={option} />
                                </Combobox.ListItemLeadingIcon>
                            ) : null}
                            <Combobox.ListItemTitle>{option.name}</Combobox.ListItemTitle>
                        </Combobox.ListItem>
                    )}
                </Combobox.List>
            </Combobox.Popover>
        </Combobox>
    );
};
